import type { SlideUpMessage } from "@braze/web-sdk";
import type { InferPayload, appToWebEvents } from "@picklebet/messaging-bridge";
import { CommentsIcon, toast } from "@picklebet/ui";
import { navigate } from "utilities/navigate";
import { getRelativeUrl } from "utilities/helpers";
import { getBridge } from "@/utilities/bridge";

export type InAppMessageType = InferPayload<typeof appToWebEvents.customIAM>;

type CustomIAM = InAppMessageType | SlideUpMessage;

const isSdkMessage = (message: CustomIAM): message is SlideUpMessage =>
  !!window.braze && message instanceof window.braze.SlideUpMessage;

export const showCustomIAM = (inAppMessage: CustomIAM) => {
  const message = inAppMessage?.message;

  if (!message) return;

  if (isSdkMessage(inAppMessage)) {
    window?.braze?.logInAppMessageImpression(inAppMessage);
  } else {
    getBridge().send.iamImpression(inAppMessage);
  }

  // if received from the app, the duration is a string. We need to convert it to a number
  const parsedDuration =
    typeof inAppMessage?.duration === "string"
      ? parseInt(inAppMessage.duration)
      : inAppMessage?.duration;

  // if we have duration and its -1, set to Infinity
  // if it's a value and its > 0, use it
  // default to 20000
  const duration =
    (parsedDuration === -1 && Infinity) || parsedDuration || 20000;

  const extrasVariant = inAppMessage?.extras?.variant;
  const extrasPriority = inAppMessage?.extras?.priority;

  // "tips" isn't a value we define anywhere - but it's freeform text a marketer
  // could still type into Braze's campaign composer (no schema their end),
  // keeping this as a defensive fallback for legacy/cloned campaigns, not a
  // real variant. New campaigns should set variant and priority independently.
  const isLegacyTips = !extrasVariant || extrasVariant === "tips";

  const { dismiss } = toast({
    description: message,
    variant: (isLegacyTips ? "info" : extrasVariant) as any,
    icon: isLegacyTips ? CommentsIcon : undefined,
    priority: isLegacyTips || extrasPriority === "low" ? "low" : "default",
    onClick: () => {
      if (inAppMessage.uri) {
        if (isSdkMessage(inAppMessage)) {
          window?.braze?.logInAppMessageClick(inAppMessage);
        } else {
          getBridge().send.iamClick(inAppMessage);
        }

        // close slideup
        dismiss();

        // if the url contains picklebet.com domain, then we only use the relative path.
        // otherwise we use the full url
        navigate(getRelativeUrl(inAppMessage?.uri));
      }
    },
    duration,
  });
};
